import type { TranslatedString } from '@interfaces/shared';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { branchKey } from '@utils/workflowBranchKey';
import { Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  isChoiceEnabled,
  modelPortfolioEditChoices,
} from './modelPortfolioEditChoices';

export type SelectModelPortfolioEditTypeOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
} & Record<string, any>;

export type SelectModelPortfolioEditTypeVisualProps = {
  options: SelectModelPortfolioEditTypeOptions;
  loading?: boolean;
  /** Receives the branch key of the chosen edit type. */
  onNext: (branchKey: string) => void | Promise<void>;
};

export const SelectModelPortfolioEditTypeVisual = ({
  options,
  loading = false,
  onNext,
}: SelectModelPortfolioEditTypeVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const [selectedBranchKey, setSelectedBranchKey] = useState('');

  const choices = modelPortfolioEditChoices
    .map((choice, index) => ({
      value: branchKey(index),
      label: translateBackendString(options?.[choice.labelKey]),
      subtitle: translateBackendString(options?.[choice.descriptionKey]),
      enabled: isChoiceEnabled(options, choice.enableKey),
    }))
    .filter((choice) => choice.enabled);

  return (
    <WorkflowCompletionForm
      onSubmit={() => onNext(selectedBranchKey)}
      testId='select-model-portfolio-edit-type-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: !selectedBranchKey || loading,
        dataTestId: 'select-model-portfolio-edit-type-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      <SelectionTile
        testId='model-portfolio-edit-type'
        value={selectedBranchKey}
        onChange={(event: any) => setSelectedBranchKey(event.target.value)}
        options={choices}
      />
    </WorkflowCompletionForm>
  );
};

export default SelectModelPortfolioEditTypeVisual;
